import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { ArrowRight, Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { buildMailtoUrl, CONTACT_EMAIL, submitWebsiteForm } from '@/lib/siteConfig';

export default function ContactPage() {
  const [contactForm, setContactForm] = useState({ name: '', email: '', company: '', phone: '', subject: '', message: '', website: '' });
  const [consultForm, setConsultForm] = useState({ name: '', email: '', company: '', phone: '', industry: '', preferred_date: '', preferred_time: '', message: '', website: '' });
  const [contactStatus, setContactStatus] = useState(null);
  const [consultStatus, setConsultStatus] = useState(null);
  const [contactLoading, setContactLoading] = useState(false);
  const [consultLoading, setConsultLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(undefined);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactStatus(null);

    try {
      await submitWebsiteForm({
        subject: `Website inquiry: ${contactForm.subject || 'General Inquiry'}`,
        replyTo: contactForm.email,
        honeypot: contactForm.website,
        fields: {
          'Form Type': 'General Inquiry',
          Name: contactForm.name,
          Email: contactForm.email,
          Company: contactForm.company,
          Phone: contactForm.phone,
          Subject: contactForm.subject,
          Message: contactForm.message,
          'Page URL': window.location.href,
        },
      });

      setContactStatus({ type: 'success', msg: 'Thank you. Your message has been sent successfully.' });
      setContactForm({ name: '', email: '', company: '', phone: '', subject: '', message: '', website: '' });
    } catch {
      setContactStatus({
        type: 'error',
        msg: 'We could not send your message automatically. Please try again or use the email link below.',
      });
    } finally {
      setContactLoading(false);
    }
  };

  const handleConsultSubmit = async (e) => {
    e.preventDefault();
    setConsultLoading(true);
    setConsultStatus(null);
    const preferredDate = selectedDate ? selectedDate.toISOString().split('T')[0] : '';

    try {
      await submitWebsiteForm({
        subject: 'Website consultation request',
        replyTo: consultForm.email,
        honeypot: consultForm.website,
        fields: {
          'Form Type': 'Consultation Request',
          Name: consultForm.name,
          Email: consultForm.email,
          Company: consultForm.company,
          Phone: consultForm.phone,
          Industry: consultForm.industry,
          'Preferred Date': preferredDate,
          'Preferred Time': consultForm.preferred_time,
          Message: consultForm.message,
          'Page URL': window.location.href,
        },
      });

      setConsultStatus({ type: 'success', msg: 'Thank you. Your consultation request has been sent successfully.' });
      setConsultForm({ name: '', email: '', company: '', phone: '', industry: '', preferred_date: '', preferred_time: '', message: '', website: '' });
      setSelectedDate(undefined);
    } catch {
      setConsultStatus({
        type: 'error',
        msg: 'We could not send your request automatically. Please try again or use the email link below.',
      });
    } finally {
      setConsultLoading(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <section data-testid="contact-hero" className="py-24 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Contact Us</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-navy leading-[1.1] mb-6">
            Let's Build the Future<br />Together
          </h1>
          <p className="text-base md:text-lg text-navy/60 max-w-2xl leading-relaxed">
            Whether you have a specific AI challenge or want to explore opportunities, we're here to help.
          </p>
        </div>
      </section>

      {/* Contact Info + Forms */}
      <section data-testid="contact-forms" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Left: Contact Info */}
            <div className="lg:col-span-4">
              <h2 className="font-heading text-2xl font-medium text-navy mb-8">Get in Touch</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-navy">Email</p>
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-navy/50 hover:text-teal transition-colors">
                      {CONTACT_EMAIL}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <Phone className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-navy">Phone</p>
                    <a href="tel:+917003260866" className="text-sm text-navy/50 hover:text-teal transition-colors">
                      +91 7003260866
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-sm bg-teal/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-teal" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-navy">Office</p>
                    <p className="text-sm text-navy/50">17, Pratapaditya Place<br />Kolkata - 700026</p>
                  </div>
                </div>
              </div>

              <div className="mt-12 p-6 bg-navy rounded-sm">
                <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-3">Privacy & Data Use</p>
                <p className="text-white/60 text-xs leading-relaxed">
                  Form details are securely forwarded to our business email through a third-party form delivery service. We use them only to respond to your request. Read our{' '}
                  <Link to="/privacy-policy" className="text-teal hover:text-teal/80">Privacy Policy</Link>.
                </p>
              </div>
            </div>

            {/* Right: Forms */}
            <div className="lg:col-span-8">
              <Tabs defaultValue="inquiry" className="w-full">
                <TabsList data-testid="contact-tabs" className="grid w-full grid-cols-2 mb-8 bg-secondary rounded-full h-12">
                  <TabsTrigger data-testid="tab-inquiry" value="inquiry" className="rounded-full text-sm data-[state=active]:bg-navy data-[state=active]:text-white">
                    General Inquiry
                  </TabsTrigger>
                  <TabsTrigger data-testid="tab-consultation" value="consultation" className="rounded-full text-sm data-[state=active]:bg-navy data-[state=active]:text-white">
                    Schedule Consultation
                  </TabsTrigger>
                </TabsList>

                {/* Inquiry Form */}
                <TabsContent value="inquiry">
                  {contactStatus?.type === 'success' ? (
                    <div data-testid="contact-success" className="text-center py-16">
                      <CheckCircle className="h-12 w-12 text-teal mx-auto mb-4" />
                      <h3 className="font-heading text-xl text-navy mb-2">Message Sent</h3>
                      <p className="text-navy/50 text-sm">{contactStatus.msg}</p>
                      <Button onClick={() => setContactStatus(null)} variant="outline" className="mt-6 rounded-full">
                        Send Another Message
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleContactSubmit} data-testid="contact-form" className="space-y-6">
                      <input
                        type="text"
                        name="_honey"
                        value={contactForm.website}
                        onChange={e => setContactForm(p => ({ ...p, website: e.target.value }))}
                        className="hidden"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="contact-name" className="text-navy text-sm mb-2 block">Full Name *</Label>
                          <Input
                            id="contact-name"
                            data-testid="contact-name"
                            required
                            value={contactForm.name}
                            onChange={e => setContactForm(p => ({ ...p, name: e.target.value }))}
                            placeholder="John Smith"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-email" className="text-navy text-sm mb-2 block">Email *</Label>
                          <Input
                            id="contact-email"
                            data-testid="contact-email"
                            type="email"
                            required
                            value={contactForm.email}
                            onChange={e => setContactForm(p => ({ ...p, email: e.target.value }))}
                            placeholder="john@company.com"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="contact-company" className="text-navy text-sm mb-2 block">Company</Label>
                          <Input
                            id="contact-company"
                            data-testid="contact-company"
                            value={contactForm.company}
                            onChange={e => setContactForm(p => ({ ...p, company: e.target.value }))}
                            placeholder="Acme Corp"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="contact-phone" className="text-navy text-sm mb-2 block">Phone</Label>
                          <Input
                            id="contact-phone"
                            data-testid="contact-phone"
                            value={contactForm.phone}
                            onChange={e => setContactForm(p => ({ ...p, phone: e.target.value }))}
                            placeholder="+91 7003260866"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="contact-subject" className="text-navy text-sm mb-2 block">Subject</Label>
                        <Input
                          id="contact-subject"
                          data-testid="contact-subject"
                          value={contactForm.subject}
                          onChange={e => setContactForm(p => ({ ...p, subject: e.target.value }))}
                          placeholder="How can we help?"
                          className="bg-secondary border-0 rounded-sm"
                        />
                      </div>
                      <div>
                        <Label htmlFor="contact-message" className="text-navy text-sm mb-2 block">Message *</Label>
                        <Textarea
                          id="contact-message"
                          data-testid="contact-message"
                          required
                          rows={5}
                          value={contactForm.message}
                          onChange={e => setContactForm(p => ({ ...p, message: e.target.value }))}
                          placeholder="Tell us about your AI challenges..."
                          className="bg-secondary border-0 rounded-sm resize-none"
                        />
                      </div>
                      {contactStatus?.type === 'error' && (
                        <p data-testid="contact-error" className="text-sm text-red-600" role="alert">
                          {contactStatus.msg}{' '}
                          <a
                            href={buildMailtoUrl(contactForm.subject || 'General Inquiry', {
                              Name: contactForm.name,
                              Email: contactForm.email,
                              Company: contactForm.company,
                              Phone: contactForm.phone,
                              Message: contactForm.message,
                            })}
                            className="underline font-medium"
                          >
                            Email us directly.
                          </a>
                        </p>
                      )}
                      <Button
                        type="submit"
                        data-testid="contact-submit"
                        disabled={contactLoading}
                        className="bg-navy text-white hover:bg-navy/90 rounded-full px-8 py-3 text-base h-auto"
                      >
                        {contactLoading ? 'Sending...' : 'Send Message'} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </form>
                  )}
                </TabsContent>

                {/* Consultation Form */}
                <TabsContent value="consultation">
                  {consultStatus?.type === 'success' ? (
                    <div data-testid="consult-success" className="text-center py-16">
                      <CheckCircle className="h-12 w-12 text-teal mx-auto mb-4" />
                      <h3 className="font-heading text-xl text-navy mb-2">Request Sent</h3>
                      <p className="text-navy/50 text-sm">{consultStatus.msg}</p>
                      <Button onClick={() => setConsultStatus(null)} variant="outline" className="mt-6 rounded-full">
                        Schedule Another
                      </Button>
                    </div>
                  ) : (
                    <form onSubmit={handleConsultSubmit} data-testid="consultation-form" className="space-y-6">
                      <input
                        type="text"
                        name="_honey"
                        value={consultForm.website}
                        onChange={e => setConsultForm(p => ({ ...p, website: e.target.value }))}
                        className="hidden"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="consult-name" className="text-navy text-sm mb-2 block">Full Name *</Label>
                          <Input
                            id="consult-name"
                            data-testid="consult-name"
                            required
                            value={consultForm.name}
                            onChange={e => setConsultForm(p => ({ ...p, name: e.target.value }))}
                            placeholder="John Smith"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="consult-email" className="text-navy text-sm mb-2 block">Email *</Label>
                          <Input
                            id="consult-email"
                            data-testid="consult-email"
                            type="email"
                            required
                            value={consultForm.email}
                            onChange={e => setConsultForm(p => ({ ...p, email: e.target.value }))}
                            placeholder="john@company.com"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="consult-company" className="text-navy text-sm mb-2 block">Company</Label>
                          <Input
                            id="consult-company"
                            data-testid="consult-company"
                            value={consultForm.company}
                            onChange={e => setConsultForm(p => ({ ...p, company: e.target.value }))}
                            placeholder="Acme Corp"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                        <div>
                          <Label htmlFor="consult-industry" className="text-navy text-sm mb-2 block">Industry</Label>
                          <Select value={consultForm.industry} onValueChange={v => setConsultForm(p => ({ ...p, industry: v }))}>
                            <SelectTrigger data-testid="consult-industry" className="bg-secondary border-0 rounded-sm">
                              <SelectValue placeholder="Select industry" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Retail">Retail</SelectItem>
                              <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                              <SelectItem value="Energy & Utilities">Energy & Utilities</SelectItem>
                              <SelectItem value="Telecom">Telecom</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <Label className="text-navy text-sm mb-2 block">Preferred Date</Label>
                        <div className="flex justify-center bg-secondary rounded-sm p-4">
                          <Calendar
                            data-testid="consult-calendar"
                            mode="single"
                            selected={selectedDate}
                            onSelect={setSelectedDate}
                            disabled={(date) => date < new Date()}
                            className="rounded-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <Label htmlFor="consult-time" className="text-navy text-sm mb-2 block">Preferred Time</Label>
                          <Select value={consultForm.preferred_time} onValueChange={v => setConsultForm(p => ({ ...p, preferred_time: v }))}>
                            <SelectTrigger data-testid="consult-time" className="bg-secondary border-0 rounded-sm">
                              <SelectValue placeholder="Select time" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="9:00 AM">9:00 AM</SelectItem>
                              <SelectItem value="10:00 AM">10:00 AM</SelectItem>
                              <SelectItem value="11:00 AM">11:00 AM</SelectItem>
                              <SelectItem value="1:00 PM">1:00 PM</SelectItem>
                              <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                              <SelectItem value="3:00 PM">3:00 PM</SelectItem>
                              <SelectItem value="4:00 PM">4:00 PM</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="consult-phone" className="text-navy text-sm mb-2 block">Phone</Label>
                          <Input
                            id="consult-phone"
                            data-testid="consult-phone"
                            value={consultForm.phone}
                            onChange={e => setConsultForm(p => ({ ...p, phone: e.target.value }))}
                            placeholder="+91 7003260866"
                            className="bg-secondary border-0 rounded-sm"
                          />
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="consult-message" className="text-navy text-sm mb-2 block">Additional Details</Label>
                        <Textarea
                          id="consult-message"
                          data-testid="consult-message"
                          rows={4}
                          value={consultForm.message}
                          onChange={e => setConsultForm(p => ({ ...p, message: e.target.value }))}
                          placeholder="What would you like to discuss?"
                          className="bg-secondary border-0 rounded-sm resize-none"
                        />
                      </div>
                      {consultStatus?.type === 'error' && (
                        <p data-testid="consult-error" className="text-sm text-red-600" role="alert">
                          {consultStatus.msg}{' '}
                          <a
                            href={buildMailtoUrl('Consultation Request', {
                              Name: consultForm.name,
                              Email: consultForm.email,
                              Company: consultForm.company,
                              Phone: consultForm.phone,
                              Industry: consultForm.industry,
                              'Preferred Date': selectedDate ? selectedDate.toISOString().split('T')[0] : '',
                              'Preferred Time': consultForm.preferred_time,
                              Message: consultForm.message,
                            })}
                            className="underline font-medium"
                          >
                            Email us directly.
                          </a>
                        </p>
                      )}
                      <Button
                        type="submit"
                        data-testid="consult-submit"
                        disabled={consultLoading}
                        className="bg-teal text-white hover:bg-teal/90 rounded-full px-8 py-3 text-base h-auto"
                      >
                        {consultLoading ? 'Sending...' : 'Send Consultation Request'} <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </form>
                  )}
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
